import { UseQueryResult } from "@tanstack/react-query";
import client from "../client";
import {
  CompanyFoodDeal,
  CreateDealForm,
  DateString,
  FoodAdminOverview,
  FoodCompany,
  FoodUser,
  FoodUserBalancesResponse,
  DisabledFoodWallet,
  RefundFoodBalanceResponse,
  MillumWeeklyMenu,
  SimpleFoodCompany,
  TimeframeFilterValue,
  UsersFoodDeal,
  SnackSupplier,
} from "@/types";
import { useBuildingQuery } from "@/components/RouterSetup";

export type FoodSupplierSettings = Partial<
  Pick<
    SnackSupplier,
    "name" | "address" | "email" | "phone" | "logoUrl" | "externalLocationId"
  >
> & {
  wlcmFoodSettings?: Partial<
    Pick<
      NonNullable<SnackSupplier["wlcmFoodSettings"]>,
      "openingLunchTime" | "closingLunchTime" | "guestLunchGrossPrice"
    >
  >;
};

export const updateFoodSupplierSettings = async (
  data: FoodSupplierSettings,
) => {
  const response = await client.put("/updateFoodSupplierSettings", data);
  return response.data as SnackSupplier;
};

export const useWeeklyMenu = (
  millumGLN: string,
): UseQueryResult<MillumWeeklyMenu> => {
  return useBuildingQuery({
    queryKey: ["weekly-menu", millumGLN],
    queryFn: async () => {
      try {
        const response = await client.get(
          `/getWeeklyMenu?millumGLN=${millumGLN}`,
        );
        return response.data;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    enabled: !!millumGLN,
  });
};

export const allFoodCompaniesOptions = {
  queryKey: ["food-companies"],
  queryFn: async () => {
    try {
      const response = await client.get(`/getAllFoodCompanies`);
      return response.data as FoodCompany[];
    } catch (error) {
      console.error(error);
      return [];
    }
  },
};

export const foodCompanyOptions = (buildingTenantId?: string) => ({
  queryKey: ["food-company", buildingTenantId],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getFoodCompanyDetails?buildingTenantId=${buildingTenantId}`,
      );
      return response.data as FoodCompany;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  enabled: !!buildingTenantId,
});

export const useFoodCompany = (buildingTenantId?: string) => {
  return useBuildingQuery(foodCompanyOptions(buildingTenantId));
};

export const simpleFoodCompanyOptions = (buildingTenantId?: string) => ({
  queryKey: ["simple-food-company", buildingTenantId],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getFoodCompanyById?buildingTenantId=${buildingTenantId}`,
      );
      return response.data as SimpleFoodCompany;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  enabled: !!buildingTenantId,
});

export const useSimpleFoodCompany = (buildingTenantId?: string) => {
  return useBuildingQuery(simpleFoodCompanyOptions(buildingTenantId));
};

export const foodUserOptions = (
  userId: string,
  buildingTenantId: string,
  supplierCompanyId?: string,
  userLanguage?: string,
) => ({
  queryKey: ["food-user", userId, buildingTenantId],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getFoodUser?userId=${userId}&buildingTenantId=${buildingTenantId}&supplierCompanyId=${supplierCompanyId}${userLanguage ? `&userLanguage=${userLanguage}` : ""}`,
      );
      return response.data as FoodUser;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  enabled: !!userId && !!buildingTenantId && !!supplierCompanyId,
  staleTime: 1000 * 60 * 60, // 1 hour
  refetchOnWindowFocus: false,
});

export const useFoodUser = (
  userId: string,
  buildingTenantId: string,
  supplierCompanyId?: string,
  userLanguage?: string,
  enabled = true,
) => {
  return useBuildingQuery({
    ...foodUserOptions(
      userId,
      buildingTenantId,
      supplierCompanyId,
      userLanguage,
    ),
    enabled: enabled,
  });
};

export const usersFoodDealOptions = (userId: string) => ({
  queryKey: ["food-deal", userId],
  queryFn: async () => {
    try {
      const response = await client.get(`/getUsersFoodDeal?userId=${userId}`);
      return response.data as UsersFoodDeal;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
  enabled: !!userId,
});

export const useUsersFoodDeal = (userId: string, enabled = true) => {
  return useBuildingQuery({ ...usersFoodDealOptions(userId), enabled });
};

export const foodQROptions = (userId: string) => ({
  queryKey: ["food-qr", userId],
  queryFn: async () => {
    try {
      const response = await client.get(`/getSignedFoodQR?userId=${userId}`);
      return response.data?.qrCode ?? ("" as string);
    } catch (error) {
      console.error(error);
      return "";
    }
  },
});

export const useFoodQR = (userId: string) => {
  return useBuildingQuery(foodQROptions(userId));
};

export const foodWalletBadgeOptions = (userId: string) => ({
  queryKey: ["food-wallet-badge", userId],
  queryFn: async () => {
    try {
      const response = await client.get(
        `/getLunchWalletBadge?userId=${userId}`,
      );
      return response.data?.passUrl ?? ("" as string);
    } catch (error) {
      console.error(error);
      return "";
    }
  },
  staleTime: Infinity,
});

export const useFoodWalletBadge = (userId: string): UseQueryResult<string> => {
  return useBuildingQuery(foodWalletBadgeOptions(userId));
};

export const foodAdminOverviewOptions = (timeframe: TimeframeFilterValue) => ({
  queryKey: ["food-admin-overview", timeframe],
  queryFn: async () => {
    try {
      const timeFilterString =
        typeof timeframe === "object"
          ? `from=${timeframe.from}&to=${timeframe.to}`
          : `timeframe=${timeframe}`;
      const response = await client.get(
        `/getFoodAdminOverview?${timeFilterString}`,
      );
      return response.data as FoodAdminOverview;
    } catch (error) {
      console.error(error);
      return null;
    }
  },
});

export const useFoodAdminOverview = (timeframe: TimeframeFilterValue) => {
  return useBuildingQuery(foodAdminOverviewOptions(timeframe));
};

export const getFoodUserBalancesAsOf = async (
  asOf: DateString,
): Promise<FoodUserBalancesResponse | null> => {
  try {
    const response = await client.get("/getFoodUserBalances", {
      params: { asOf },
    });
    return response.data as FoodUserBalancesResponse;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const createFoodDeal = async (deal: CreateDealForm) => {
  return client.post(`/createFoodDeal`, deal);
};

export const updateCompanyFoodDeals = async (
  buildingTenantId: string,
  foodDeals: CompanyFoodDeal[],
  lunchCreditAmount?: number,
) => {
  return await client.patch(`/updateCompanyFoodDeals`, {
    buildingTenantId,
    foodDeals,
    lunchCreditAmount,
  });
};

export const updateFoodUser = async ({
  buildingTenantId,
  userId,
  newDealIds,
  disableUser,
}: {
  buildingTenantId: string;
  userId: string;
  newDealIds?: string[];
  disableUser?: boolean;
}) => {
  return await client.patch(`/updateFoodUser`, {
    buildingTenantId,
    userId,
    newDealIds,
    disableUser,
  });
};

export const topupFoodBalance = async ({
  userId,
  amount,
  idempotencyKey,
}: {
  userId: string;
  amount: number;
  idempotencyKey: string;
}) => {
  return await client.post(`/topupFoodBalance`, {
    userId,
    amount,
    idempotencyKey,
  });
};

export const refundFoodBalance = async ({
  userId,
  amount,
  description,
  idempotencyKey,
}: {
  userId: string;
  amount: number;
  description: string;
  idempotencyKey: string;
}) => {
  const response = await client.post<{ data: RefundFoodBalanceResponse }>(
    `/refundFoodBalance`,
    {
      userId,
      amount,
      description,
      idempotencyKey,
    },
  );
  return response.data.data;
};

export const disabledFoodWalletsOptions = (supplierCompanyId?: string) => ({
  queryKey: ["disabled-food-wallets", supplierCompanyId],
  queryFn: async () => {
    const response = await client.get<{ wallets: DisabledFoodWallet[] }>(
      "/getDisabledFoodWallets",
    );
    return response.data.wallets ?? [];
  },
  enabled: !!supplierCompanyId,
  staleTime: 1000 * 60 * 5,
});

export type GetMollieAuthUrlResponse =
  { authorizationUrl: string } | { alreadyConnected: true };

export const getMollieAuthUrl = async (): Promise<GetMollieAuthUrlResponse> => {
  const { data } =
    await client.get<GetMollieAuthUrlResponse>("/getMollieAuthUrl");
  return data;
};

export const handleMollieOAuthCallback = async (
  code: string,
  state: string,
): Promise<{ redirectUrl: string }> => {
  const { data } = await client.post<{ redirectUrl: string }>(
    "/handleMollieOAuthCallback",
    { code, state },
  );
  return data;
};

export const getTransactionReceipt = async (
  userId: string,
  transactionId: string,
): Promise<Blob> => {
  const { data } = await client.get("/getTransactionReceipt", {
    params: { userId, transactionId },
    responseType: "blob",
  });
  return data as Blob;
};
